/**
 * Response unwrapping for the UCC hooks.
 *
 * Each UCC hook module carried its own copy of an `unwrap` that threw a bare
 * Error, so a page could print the message but could not tell a 404 (the
 * service answered nothing at that path) from a 500 or a validation failure.
 * The Submissions and Search pages need that distinction: their backends have
 * no production deployment yet (GH #1734), and a 404 there means "not
 * deployed", which the page says in words instead of rendering an empty list.
 */

import { apiErrorMessage } from '../api/error-message';

/** A failed UCC read or write, carrying the HTTP status the service returned. */
export class UccRequestError extends Error {
  /** HTTP status of the failed response; 0 when no response arrived. */
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'UccRequestError';
    this.status = status;
  }
}

/**
 * Return the response body, or throw a {@link UccRequestError} when the
 * request failed or returned no body.
 */
export function unwrapUccResponse<T>(result: {
  data?: T;
  error?: unknown;
  response: Response;
}): T {
  if (result.error || result.data === undefined || result.data === null) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText || 'Request failed';
    throw new UccRequestError(
      apiErrorMessage(result.error) ?? `HTTP ${status}: ${statusText}`,
      status,
    );
  }
  return result.data;
}

/** The HTTP status carried by an error thrown from a UCC hook, if any. */
export function uccErrorStatus(error: unknown): number | undefined {
  return error instanceof UccRequestError ? error.status : undefined;
}

/** Retries a UCC read makes after a failure it can recover from; the app default in main.tsx. */
export const UCC_READ_RETRIES = 3;

/**
 * React Query `retry` for UCC reads: retry a network failure or a 5xx, never a
 * 4xx. The app default retries every failure three times with 1 s, 2 s and 4 s
 * delays, so a 404 from an undeployed service held a page on its skeleton for
 * about 7 s before the page could say the service is not there (GH #1734).
 * A 4xx answers the same way on every retry.
 *
 * `error` is typed Error, the query's error type: typed unknown, it widened
 * the error type React Query infers for every hook that passes this.
 */
export function retryUnlessClientError(failureCount: number, error: Error): boolean {
  const status = uccErrorStatus(error);
  const recoverable = status === undefined || status === 0 || status >= 500;
  return recoverable && failureCount < UCC_READ_RETRIES;
}
